import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Input,
    RangeSlider,
    RangeSliderTrack,
    RangeSliderFilledTrack,
    RangeSliderThumb,
    Text,
    Divider
} from '@chakra-ui/react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const PRICE_RANGE_THRESHOLD = 5

const RangeSliderRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const [bounds, setBounds] = useState([0, 300])
    const [range, setRange] = useState([0, 300])
    const [inputMin, setInputMin] = useState('')
    const [inputMax, setInputMax] = useState('')
    const intl = useIntl()
    const {currency} = useCurrency()
    const lastAppliedRange = useRef(range)

    const formatCurrency = (value) => {
        if (value === '' || isNaN(value)) return ''
        return intl.formatNumber(Number(value), {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
            useGrouping: false
        })
    }

    const parseCurrency = (formattedValue) => {
        const numeric = parseFloat(formattedValue.replace(/[^\d.-]/g, ''))
        return isNaN(numeric) ? null : numeric
    }

    const updateInputFields = (range) => {
        setInputMin(formatCurrency(range[0]))
        setInputMax(formatCurrency(range[1]))
    }

    useEffect(() => {
        updateInputFields(range)
    }, [range])

    useEffect(() => {
        if (!selectedFilters?.length) {
            lastAppliedRange.current = null
        }
    }, [selectedFilters])

    useEffect(() => {
        const defaultValue = filter.values?.[0]?.value
        const match = defaultValue?.match(/\((\d+(?:\.\d+)?)\.\.(\d+(?:\.\d+)?)\)/)

        if (match) {
            const [min, max] = [parseFloat(match[1]), parseFloat(match[2])]
            setBounds([min, max])

            const selected = selectedFilters?.[0]
            const selectedMatch = selected?.match(/\((\d+(?:\.\d+)?)\.\.(\d+(?:\.\d+)?)\)/)

            if (selectedMatch) {
                const selectedRange = [parseFloat(selectedMatch[1]), parseFloat(selectedMatch[2])]
                setRange(selectedRange)
            } else {
                setRange([min, max])
            }
        }
    }, [filter.values, selectedFilters])

    const updateRange = (newRange) => {
        const [min, max] = newRange
        if (max - min >= PRICE_RANGE_THRESHOLD) {
            setRange(newRange)
        }
    }

    const handleChangeEnd = (finalRange) => {
        const [min, max] = finalRange
        if (max - min >= PRICE_RANGE_THRESHOLD) {
            if (
                !lastAppliedRange.current ||
                lastAppliedRange.current[0] !== min ||
                lastAppliedRange.current[1] !== max
            ) {
                const value = `(${min}..${max})`
                toggleFilter(
                    {label: `${formatCurrency(min)} - ${formatCurrency(max)}`, value},
                    filter.attributeId,
                    false,
                    false
                )
                lastAppliedRange.current = [min, max]
            }
        }
    }

    const handleInputChange = (index, rawValue) => {
        const numericValue = parseCurrency(rawValue)

        if (numericValue !== null) {
            const formatted = formatCurrency(numericValue)
            index === 0 ? setInputMin(formatted) : setInputMax(formatted)
        } else {
            index === 0 ? setInputMin('') : setInputMax('')
        }
    }

    const handleInputBlur = (index) => {
        const minRaw = parseCurrency(inputMin)
        const maxRaw = parseCurrency(inputMax)

        let minVal = minRaw ?? bounds[0]
        let maxVal = maxRaw ?? bounds[1]

        minVal = Math.max(bounds[0], Math.min(minVal, bounds[1]))
        maxVal = Math.min(bounds[1], Math.max(maxVal, bounds[0]))

        if (maxVal - minVal < PRICE_RANGE_THRESHOLD) {
            if (index === 0) {
                minVal = Math.max(bounds[0], maxVal - PRICE_RANGE_THRESHOLD)
            } else {
                maxVal = Math.min(bounds[1], minVal + PRICE_RANGE_THRESHOLD)
            }
        }

        const newRange = [minVal, maxVal]
        setRange(newRange)
        updateInputFields(newRange)

        // Trigger toggleFilter only if range changed
        if (
            !lastAppliedRange.current ||
            lastAppliedRange.current[0] !== minVal ||
            lastAppliedRange.current[1] !== maxVal
        ) {
            const value = `(${minVal}..${maxVal})`
            toggleFilter(
                {label: `${formatCurrency(minVal)} - ${formatCurrency(maxVal)}`, value},
                filter.attributeId,
                false,
                false
            )
            lastAppliedRange.current = [minVal, maxVal]
        }
    }

    return (
        <Box>
            <Box px="6px">
                <RangeSlider
                    aria-label={['Min price', 'Max price']}
                    min={bounds[0]}
                    max={bounds[1]}
                    value={range}
                    onChange={updateRange}
                    onChangeEnd={handleChangeEnd}
                    step={1}
                    minStepsBetweenThumbs={PRICE_RANGE_THRESHOLD}
                    sx={{'--slider-thumb-size': '12px'}}
                >
                    <RangeSliderTrack h="1px" bg="grey.400">
                        <RangeSliderFilledTrack h="1px" bg="black" />
                    </RangeSliderTrack>
                    <RangeSliderThumb
                        index={0}
                        bg="black"
                        sx={{_active: {transform: 'translateY(-50%)'}}}
                    />
                    <RangeSliderThumb
                        index={1}
                        bg="black"
                        sx={{_active: {transform: 'translateY(-50%)'}}}
                    />
                </RangeSlider>
            </Box>

            <Flex gap="56px" mt={4} alignItems="center">
                {[inputMin, inputMax].map((val, index) => (
                    <React.Fragment key={index}>
                        {index === 1 && (
                            <Divider
                                orientation="horizontal"
                                height="1px"
                                w="16px"
                                bg="grey.800"
                                alignSelf="center"
                            />
                        )}
                        <Box flex={1} py="5px" px="8px" border="1px solid" borderColor="grey.400">
                            <Flex flexDirection="column" alignItems="start" gap="8px">
                                <Text fontSize="xs">
                                    {index === 0 ? (
                                        <FormattedMessage
                                            defaultMessage="Minimum"
                                            id="filters.price.min"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Maximum"
                                            id="filters.price.max"
                                        />
                                    )}
                                </Text>
                                <Input
                                    type="text"
                                    value={val}
                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                    onBlur={() => handleInputBlur(index)}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    borderBottom="none"
                                />
                            </Flex>
                        </Box>
                    </React.Fragment>
                ))}
            </Flex>
        </Box>
    )
}

RangeSliderRefinements.propTypes = {
    filter: PropTypes.object.isRequired,
    toggleFilter: PropTypes.func.isRequired,
    selectedFilters: PropTypes.array
}

export default RangeSliderRefinements
